import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  alpha,
  useTheme,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Skeleton,
  Grid,
  InputAdornment,
  IconButton,
  Menu,
  Tooltip,
  Pagination,
  LinearProgress,
} from "@mui/material";
import {
  useGetInvoiceQuery,
  useSendInvoiceMutation,
  useDeleteInvoiceMutation,
  useGetInvoiceSummaryQuery,
  useDuplicateInvoiceMutation,
  useMarkInvoiceSentMutation,
  useGetallLocationQuery,
} from "../../Api/attoDeskApi";
import {
  openInvoiceListPrint,
  openInvoiceListExport,
  openInvoicePdf,
} from "./Invoices/printHelpers";
import { useNotifier } from "../../Core/Notifier";
import { useConfirm, confirmPresets } from "../../Components/Confirm";
import { useNavigate, useSearchParams } from "react-router-dom";
import useUser from "../../Core/hook/useUser";
import dayjs, { Dayjs } from "dayjs";
import AppDatePicker from "../../Components/DatePicker/AppDatePicker";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SendIcon from "@mui/icons-material/Send";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BlockIcon from "@mui/icons-material/Block";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";
import { StaffProfileLink, StudentProfileLink, ParentProfileLink } from "../../Components/ProfileLinks";
import RecordPaymentDialog from "./Invoices/RecordPaymentDialog";
import VoidInvoiceDialog from "./Invoices/VoidInvoiceDialog";

// ── Status metadata ─────────────────────────────────────────────────
const STATUS_META: Record<string, { label: string; bg: string; fg: string }> = {
  draft:     { label: "Draft",         bg: "#f1f5f9", fg: "#475569" },
  sent:      { label: "Sent",          bg: "#dbeafe", fg: "#1d4ed8" },
  partial:   { label: "Partially Paid", bg: "#fef3c7", fg: "#b45309" },
  paid:      { label: "Paid",          bg: "#dcfce7", fg: "#15803d" },
  overdue:   { label: "Overdue",       bg: "#fee2e2", fg: "#b91c1c" },
  voided:    { label: "Voided",        bg: "#e5e7eb", fg: "#4b5563" },
  cancelled: { label: "Cancelled",     bg: "#e5e7eb", fg: "#4b5563" },
};

const money = (v: number | string | null | undefined) =>
  `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d: string | null | undefined) => (d ? dayjs(d).format("MMM D, YYYY") : "—");

// CSV export using only what the backend returned + is authorized for.
const downloadCSV = (rows: any[], filename: string) => {
  if (!rows.length) return;
  const cols = [
    ["invoiceNumber", "Invoice #"],
    ["studentName", "Student"],
    ["parentName", "Parent"],
    ["locationName", "Location"],
    ["issueDate", "Issue Date"],
    ["dueDate", "Due Date"],
    ["totalAmount", "Amount"],
    ["paid_amount", "Paid"],
    ["balance_due", "Balance"],
    ["effective_status", "Status"],
  ];
  const header = cols.map((c) => c[1]).join(",");
  const body = rows
    .map((r) =>
      cols
        .map(([k]) => {
          const v = (r as any)[k];
          const s = v == null ? "" : String(v).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    )
    .join("\n");
  const csv = header + "\n" + body;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// ── Row action menu ─────────────────────────────────────────────────
interface RowMenuProps {
  invoice: any;
  canEdit: boolean;
  canCreate: boolean;
  canDelete: boolean;
  onView: () => void;
  onEdit: () => void;
  onSend: () => void;
  onMarkSent: () => void;
  onRecordPayment: () => void;
  onDuplicate: () => void;
  onVoid: () => void;
  onDelete: () => void;
}

const RowMenu: React.FC<RowMenuProps> = ({
  invoice,
  canEdit,
  canCreate,
  canDelete,
  onView,
  onEdit,
  onSend,
  onMarkSent,
  onRecordPayment,
  onDuplicate,
  onVoid,
  onDelete,
}) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const st = String(invoice?.effective_status || invoice?.status || "draft").toLowerCase();
  const isDraft = st === "draft";
  const isVoided = st === "voided" || st === "cancelled";
  const isPaid = st === "paid";
  const hasPayments = Number(invoice?.paid_amount || 0) > 0;
  const canPay = !isVoided && !isPaid && Number(invoice?.balance_due || 0) > 0;
  const canSend = !isVoided && canEdit;

  const close = () => setAnchor(null);
  const wrap = (fn: () => void) => () => { close(); fn(); };

  return (
    <>
      <Tooltip title="More actions">
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
          sx={{ bgcolor: "rgba(15,23,42,0.04)" }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        slotProps={{ paper: { sx: { minWidth: 220, borderRadius: "12px" } } }}
      >
        <MenuItem onClick={wrap(onView)} dense>
          <VisibilityOutlinedIcon fontSize="small" sx={{ mr: 1.25, color: "text.secondary" }} /> View Invoice
        </MenuItem>
        {canEdit && !isVoided && !isPaid && (
          <MenuItem onClick={wrap(onEdit)} dense>
            <EditOutlinedIcon fontSize="small" sx={{ mr: 1.25, color: "text.secondary" }} /> Edit Invoice
          </MenuItem>
        )}
        {canPay && (
          <MenuItem onClick={wrap(onRecordPayment)} dense>
            <PaidOutlinedIcon fontSize="small" sx={{ mr: 1.25, color: "#15803d" }} /> Record Payment
          </MenuItem>
        )}
        {canSend && (
          <MenuItem onClick={wrap(onSend)} dense>
            <SendIcon fontSize="small" sx={{ mr: 1.25, color: "#1d4ed8" }} /> {isDraft ? "Send Invoice" : "Resend Invoice"}
          </MenuItem>
        )}
        {isDraft && canEdit && (
          <MenuItem onClick={wrap(onMarkSent)} dense>
            <MarkEmailReadOutlinedIcon fontSize="small" sx={{ mr: 1.25, color: "text.secondary" }} /> Mark as Sent
          </MenuItem>
        )}
        {canCreate && (
          <MenuItem onClick={wrap(onDuplicate)} dense>
            <ContentCopyIcon fontSize="small" sx={{ mr: 1.25, color: "text.secondary" }} /> Duplicate
          </MenuItem>
        )}
        <MenuItem
          onClick={wrap(() => openInvoicePdf(invoice.id, invoice.invoiceNumber))}
          dense
        >
          <PrintIcon fontSize="small" sx={{ mr: 1.25, color: "text.secondary" }} /> Print
        </MenuItem>
        <MenuItem
          onClick={wrap(() => openInvoicePdf(invoice.id, invoice.invoiceNumber, "download"))}
          dense
        >
          <DownloadIcon fontSize="small" sx={{ mr: 1.25, color: "text.secondary" }} /> Download PDF
        </MenuItem>
        {canEdit && !isVoided && (
          <MenuItem onClick={wrap(onVoid)} dense sx={{ color: "#b45309" }}>
            <BlockIcon fontSize="small" sx={{ mr: 1.25 }} /> Void Invoice
          </MenuItem>
        )}
        {isDraft && !hasPayments && canDelete && (
          <MenuItem onClick={wrap(onDelete)} dense sx={{ color: "#dc2626" }}>
            <DeleteOutlineIcon fontSize="small" sx={{ mr: 1.25 }} /> Delete Draft
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

// ── Summary tile ────────────────────────────────────────────────────
const SummaryTile: React.FC<{
  label: string;
  value: string;
  hint?: string;
  color: string;
  icon: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
}> = ({ label, value, hint, color, icon, onClick, active }) => (
  <Box
    onClick={onClick}
    sx={{
      p: 2,
      borderRadius: "14px",
      cursor: onClick ? "pointer" : "default",
      background: `linear-gradient(180deg, ${alpha(color, 0.10)} 0%, ${alpha(color, 0.03)} 100%)`,
      border: `1px solid ${alpha(color, active ? 0.55 : 0.22)}`,
      transition: "transform .15s, box-shadow .15s, border-color .15s",
      height: "100%",
      minHeight: 128,
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      "&:hover": onClick
        ? { transform: "translateY(-2px)", boxShadow: `0 6px 18px ${alpha(color, 0.18)}`, borderColor: alpha(color, 0.45) }
        : undefined,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75, minHeight: 34 }}>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "10px",
          bgcolor: alpha(color, 0.18),
          color: color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: `0 4px 10px ${alpha(color, 0.15)}`,
          "& svg": { fontSize: 18 },
        }}
      >
        {icon}
      </Box>
    </Box>
    <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em", color, lineHeight: 1.1 }}>{value}</Typography>
    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#475569", mt: 0.5 }}>{label}</Typography>
    <Typography sx={{ fontSize: 10.5, color: "text.disabled", mt: 0.25, minHeight: 14 }}>
      {hint || " "}
    </Typography>
  </Box>
);

// ── Main page ───────────────────────────────────────────────────────
const InvoicesPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const confirm = useConfirm();
  const { canCreate, canEdit, canDelete } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const canCreateInvoice = canCreate("Invoice");
  const canEditInvoice = canEdit("Invoice");
  const canDeleteInvoice = canDelete("Invoice");

  // Bridge the framework-agnostic notify events emitted by printHelpers
  // into the project notifier. Kept scoped to this page's lifecycle so
  // teardown removes the listener.
  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<{ level: string; message: string }>).detail;
      if (!detail) return;
      if (detail.level === "error") showErrorMessage(detail.message);
      else showMessage(detail.message);
    };
    window.addEventListener("finance:notify", handler);
    return () => window.removeEventListener("finance:notify", handler);
  }, [showErrorMessage, showMessage]);

  // Filter state (URL-synced so refresh preserves context).
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [modeFilter, setModeFilter] = useState<string>(searchParams.get("mode") || "");
  const [datePreset, setDatePreset] = useState<string>(searchParams.get("preset") || (searchParams.get("from") || searchParams.get("to") ? "custom" : ""));
  const [startDate, setStartDate] = useState<string>(searchParams.get("from") || "");
  const [endDate, setEndDate] = useState<string>(searchParams.get("to") || "");
  const [overdueOnly, setOverdueOnly] = useState<boolean>(searchParams.get("overdue") === "1");
  const [locationFilter, setLocationFilter] = useState<string>(searchParams.get("location") || "");
  const [page, setPage] = useState<number>(Number(searchParams.get("page") || "1"));

  // Resolve a named date preset to a {from, to} pair in YYYY-MM-DD form.
  // Custom returns null so we don't overwrite the user's manual selections.
  const presetRange = (preset: string): { from: string; to: string } | null => {
    const today = dayjs();
    switch (preset) {
      case "today":       return { from: today.format("YYYY-MM-DD"), to: today.format("YYYY-MM-DD") };
      case "yesterday":   return { from: today.subtract(1, "day").format("YYYY-MM-DD"), to: today.subtract(1, "day").format("YYYY-MM-DD") };
      case "this_week":   return { from: today.startOf("week").format("YYYY-MM-DD"), to: today.endOf("week").format("YYYY-MM-DD") };
      case "last_week":   return { from: today.subtract(1, "week").startOf("week").format("YYYY-MM-DD"), to: today.subtract(1, "week").endOf("week").format("YYYY-MM-DD") };
      case "this_month":  return { from: today.startOf("month").format("YYYY-MM-DD"), to: today.endOf("month").format("YYYY-MM-DD") };
      case "last_month":  return { from: today.subtract(1, "month").startOf("month").format("YYYY-MM-DD"), to: today.subtract(1, "month").endOf("month").format("YYYY-MM-DD") };
      default:            return null; // "" (All) or "custom"
    }
  };
  const applyDatePreset = (preset: string) => {
    setDatePreset(preset);
    const r = presetRange(preset);
    if (preset === "custom") return;          // keep any existing from/to
    if (r) { setStartDate(r.from); setEndDate(r.to); }
    else   { setStartDate("");     setEndDate("");     } // All Dates
    setPage(1);
  };

  const DATE_PRESET_LABELS: Record<string, string> = {
    today: "Today", yesterday: "Yesterday",
    this_week: "This Week", last_week: "Last Week",
    this_month: "This Month", last_month: "Last Month",
    custom: "Custom",
  };
  const MODE_LABELS: Record<string, string> = {
    online: "Online", in_person: "In Person", hybrid: "Hybrid",
  };

  // Only locations the current user is scoped to. The endpoint is already
  // filtered by the backend permission system (super admin sees all,
  // location-assigned users see only theirs).
  const { data: locationData } = useGetallLocationQuery({ searchText: "" });
  const locations: any[] = useMemo(
    () => (Array.isArray(locationData?.data) ? (locationData!.data as any[]) : []),
    [locationData]
  );
  const perPage = 25;

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Sync filters into URL (replaces so back-button still works cleanly).
  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedSearch) next.set("q", debouncedSearch);
    if (statusFilter) next.set("status", statusFilter);
    if (modeFilter) next.set("mode", modeFilter);
    if (datePreset) next.set("preset", datePreset);
    if (startDate) next.set("from", startDate);
    if (endDate) next.set("to", endDate);
    if (overdueOnly) next.set("overdue", "1");
    if (locationFilter) next.set("location", locationFilter);
    if (page > 1) next.set("page", String(page));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statusFilter, modeFilter, datePreset, startDate, endDate, overdueOnly, locationFilter, page]);

  const listQueryArgs = {
    searchText: debouncedSearch,
    status: statusFilter,
    // mode + date_preset are forwarded to the backend so the invoice
    // list, summary, CSV export, and Print List stay in lock-step once
    // server-side filtering for these dimensions is wired up. Until
    // then the backend ignores unknown params (existing behaviour).
    mode: modeFilter || undefined,
    date_preset: datePreset || undefined,
    startDate,
    endDate,
    overdueOnly: overdueOnly ? true : undefined,
    locationId: locationFilter || undefined,
    page,
    per_page: perPage,
  };

  const { data: invData, isLoading, isFetching } = useGetInvoiceQuery(listQueryArgs);
  const { data: sumData } = useGetInvoiceSummaryQuery({
    startDate,
    endDate,
    locationId: locationFilter || undefined,
  });

  const [sendInvoice] = useSendInvoiceMutation();
  const [deleteInvoice] = useDeleteInvoiceMutation();
  const [duplicateInvoice] = useDuplicateInvoiceMutation();
  const [markInvoiceSent] = useMarkInvoiceSentMutation();

  const rows: any[] = useMemo(() => {
    const list = Array.isArray(invData?.data) ? (invData!.data as any[]) : [];
    // Enrich display fields.
    return list.map((inv) => ({
      ...inv,
      studentName: inv.student ? `${inv.student.first_name || ""} ${inv.student.last_name || ""}`.trim() : "—",
      parentName: inv.parent_info?.name || "—",
      // Prefer the backend's per-invoice aggregated location label (derived
      // from every class on the invoice: one location → name; multiple →
      // "Multiple"; all online → "Online"). Falls back to the student's
      // default location so the column is never blank when data exists.
      locationName: inv.location_label || inv.student?.location?.location_name || "—",
    }));
  }, [invData]);
  const total = (invData as any)?.pagination?.total ?? rows.length;
  const lastPage = (invData as any)?.pagination?.last_page ?? 1;

  const sum = (sumData as any)?.data || {};

  // Navigate to the dedicated invoice view page, preserving the current
  // list URL as `?return=` so Back returns to the same filters + page.
  const openInvoice = (id: number | string) => {
    const currentUrl = window.location.pathname + window.location.search;
    navigate(`/finance/invoices/${id}?return=${encodeURIComponent(currentUrl)}`);
  };

  // ── Action handlers ──
  const [payFor, setPayFor] = useState<any | null>(null);
  const [voidFor, setVoidFor] = useState<any | null>(null);

  const doSend = async (inv: any) => {
    try {
      const r: any = await sendInvoice({ id: inv.id }).unwrap();
      if (r?.status) showMessage(inv?.status === "sent" ? "Invoice resent." : "Invoice sent to the parent/guardian.");
      else showErrorMessage(r?.message || "Failed to send invoice.");
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to send invoice.");
    }
  };

  const doMarkSent = async (inv: any) => {
    try {
      const r: any = await markInvoiceSent(inv.id).unwrap();
      if (r?.status) showMessage("Marked as sent.");
      else showErrorMessage(r?.message || "Failed to mark as sent.");
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to mark as sent.");
    }
  };

  const doDuplicate = async (inv: any) => {
    try {
      const r: any = await duplicateInvoice(inv.id).unwrap();
      if (r?.status) {
        showMessage(`Duplicated as ${r?.data?.invoiceNumber}.`);
        if (r?.data?.id) openInvoice(r.data.id);
      } else {
        showErrorMessage(r?.message || "Duplicate failed.");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Duplicate failed.");
    }
  };

  const doDelete = async (inv: any) => {
    const ok = await confirm(confirmPresets.deletePermanent(`invoice ${inv.invoiceNumber}`));
    if (!ok) return;
    try {
      const r: any = await deleteInvoice(String(inv.id)).unwrap();
      if (r?.status) showMessage("Draft invoice deleted.");
      else showErrorMessage(r?.message || "Failed to delete invoice.");
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to delete invoice.");
    }
  };

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("");
    setModeFilter("");
    setDatePreset("");
    setStartDate("");
    setEndDate("");
    setOverdueOnly(false);
    setLocationFilter("");
    setPage(1);
  };

  const activeFilterCount =
    (search ? 1 : 0) +
    (statusFilter ? 1 : 0) +
    (modeFilter ? 1 : 0) +
    (datePreset ? 1 : 0) +
    (startDate ? 1 : 0) +
    (endDate ? 1 : 0) +
    (overdueOnly ? 1 : 0) +
    (locationFilter ? 1 : 0);

  // Collect the current filter set once — used by both the CSV export and
  // the Print List button so they stay in lock-step.
  const currentFilters = () => ({
    searchText: debouncedSearch,
    status: statusFilter,
    mode: modeFilter,
    date_preset: datePreset,
    startDate,
    endDate,
    overdueOnly,
    locationId: locationFilter,
  });

  // Print List is async (opens a Blob URL) — guard against double clicks
  // and give the button a visible "Preparing…" state.
  const [printing, setPrinting] = useState(false);
  const handlePrintList = async () => {
    if (printing || rows.length === 0) return;
    setPrinting(true);
    try {
      await openInvoiceListPrint(currentFilters());
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, background: "linear-gradient(180deg, #f4f8ff 0%, #f8fafc 260px, #f8fafc 100%)", minHeight: "100vh", mx: -3, mt: -3, display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Header */}
      <Box
        sx={{
          borderRadius: "18px",
          p: { xs: 2, md: 2.5 },
          background: "linear-gradient(135deg, rgba(37,99,235,0.06) 0%, rgba(22,163,74,0.05) 100%)",
          border: "1px solid rgba(15,23,42,0.06)",
          boxShadow: "0 4px 14px rgba(15,23,42,0.04)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: { md: "center" }, justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: 0.6 }}>
              Finance
            </Typography>
            <Typography sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 800, letterSpacing: "-0.02em", color: "#0f172a", lineHeight: 1.15, mt: 0.5 }}>
              Invoices
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.5 }}>
              Track invoices, record payments, generate receipts, and manage billing across all authorized locations.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
              onClick={() => openInvoiceListExport(currentFilters())}
              sx={{
                textTransform: "none", fontWeight: 700, borderRadius: "10px",
                bgcolor: "#fff", borderColor: alpha("#2563eb", 0.35), color: "#2563eb",
                height: 34, px: 1.75,
                "&:hover": { borderColor: "#2563eb", bgcolor: alpha("#2563eb", 0.06) },
              }}
            >
              Export CSV
            </Button>
            <Tooltip title={rows.length === 0 ? "No invoices available to print." : "Print every invoice matching current filters"}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
                  onClick={handlePrintList}
                  disabled={printing || rows.length === 0}
                  sx={{
                    textTransform: "none", fontWeight: 700, borderRadius: "10px",
                    bgcolor: "#fff", borderColor: alpha("#2563eb", 0.35), color: "#2563eb",
                    height: 34, px: 1.75,
                    "&:hover": { borderColor: "#2563eb", bgcolor: alpha("#2563eb", 0.06) },
                    "&.Mui-disabled": { bgcolor: "#f8fafc", borderColor: alpha("#94a3b8", 0.3), color: "#94a3b8" },
                  }}
                >
                  {printing ? "Preparing…" : "Print List"}
                </Button>
              </span>
            </Tooltip>
            {canCreateInvoice && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate("/invoice/create")}
                sx={{
                  textTransform: "none", fontWeight: 700, borderRadius: "10px",
                  background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                  boxShadow: "0 6px 16px rgba(37,99,235,0.28)",
                  height: 34, px: 1.75,
                  "&:hover": { boxShadow: "0 8px 22px rgba(37,99,235,0.36)" },
                }}
              >
                Create Invoice
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Summary cards */}
      <Grid container spacing={1.5} alignItems="stretch">
        <Grid item xs={6} sm={4} lg={2}>
          <SummaryTile label="Total Invoiced" value={money(sum.totalInvoiced)} color="#2563eb" icon={<ReceiptLongIcon />} />
        </Grid>
        <Grid item xs={6} sm={4} lg={2}>
          <SummaryTile label="Collected" value={money(sum.collected)} color="#16a34a" icon={<AttachMoneyIcon />} />
        </Grid>
        <Grid item xs={6} sm={4} lg={2}>
          <SummaryTile label="Outstanding" value={money(sum.outstanding)} color="#f59e0b" icon={<AccountBalanceWalletOutlinedIcon />} />
        </Grid>
        <Grid item xs={6} sm={4} lg={2}>
          <SummaryTile
            label="Overdue"
            hint={`${sum.overdueCount || 0} invoices`}
            value={money(sum.overdue)}
            color="#ef4444"
            icon={<WarningAmberOutlinedIcon />}
            onClick={() => { setOverdueOnly(true); setStatusFilter(""); setPage(1); }}
            active={overdueOnly}
          />
        </Grid>
        <Grid item xs={6} sm={4} lg={2}>
          <SummaryTile
            label="Draft"
            hint={`${sum.draftCount || 0} invoices`}
            value={String(sum.draftCount ?? 0)}
            color="#8b5cf6"
            icon={<DraftsOutlinedIcon />}
            onClick={() => { setStatusFilter("draft"); setOverdueOnly(false); setPage(1); }}
            active={statusFilter === "draft"}
          />
        </Grid>
        <Grid item xs={6} sm={4} lg={2}>
          <SummaryTile
            label="Paid"
            hint={`${sum.paidCount || 0} invoices`}
            value={String(sum.paidCount ?? 0)}
            color="#14b8a6"
            icon={<CheckCircleOutlineIcon />}
            onClick={() => { setStatusFilter("paid"); setOverdueOnly(false); setPage(1); }}
            active={statusFilter === "paid"}
          />
        </Grid>
      </Grid>

      {/* Filters — order enforced: Location → Status → Mode → Date Preset → Search.
          Custom From/To only appear in the secondary row when Date Preset = Custom.
          Overdue toggle, Clear Filters, active chips, and the result-count pill
          live in the same secondary row so the main row stays focused on
          picking data. */}
      {(() => {
        const customBadgeColor = (invalid: boolean) => invalid ? "#dc2626" : undefined;
        const dateRangeInvalid = !!(startDate && endDate && dayjs(startDate).isAfter(dayjs(endDate), "day"));
        // Shared control sx so height + radius + focus outline stay consistent.
        const controlSx = {
          borderRadius: "10px", height: 40, fontSize: 13, bgcolor: "#fff",
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2563eb", borderWidth: "1.5px" },
        } as const;
        return (
      <Box
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: "18px",
          bgcolor: "#fff",
          border: "1px solid rgba(15,23,42,0.06)",
          boxShadow: "0 6px 20px rgba(15,23,42,0.04)",
        }}
      >
        {/* Card header — title + short subtitle. Result count moved into the
            secondary row per spec §7/§11 so the main row is filters-only. */}
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>Filters</Typography>
          <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.25 }}>
            Narrow invoices by location, status, study mode, date range, or search.
          </Typography>
        </Box>

        {/* Main row: Location → Status → Mode → Date Preset → Search */}
        <Grid container spacing={1.5} alignItems="center">
          {locations.length > 0 && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  label="Location"
                  value={locationFilter}
                  onChange={(e) => { setLocationFilter(String(e.target.value)); setPage(1); }}
                  sx={controlSx}
                >
                  <MenuItem value="">All Locations</MenuItem>
                  {locations.map((l: any) => (
                    <MenuItem key={l.id} value={String(l.id)}>{l.location_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(String(e.target.value)); setPage(1); }}
                sx={controlSx}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="partial">Partially Paid</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Study Mode</InputLabel>
              <Select
                label="Study Mode"
                value={modeFilter}
                onChange={(e) => { setModeFilter(String(e.target.value)); setPage(1); }}
                sx={controlSx}
              >
                <MenuItem value="">All Modes</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="in_person">In Person</MenuItem>
                <MenuItem value="hybrid">Hybrid</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Date Range</InputLabel>
              <Select
                label="Date Range"
                value={datePreset}
                onChange={(e) => applyDatePreset(String(e.target.value))}
                sx={controlSx}
              >
                <MenuItem value="">All Dates</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="this_week">This Week</MenuItem>
                <MenuItem value="last_week">Last Week</MenuItem>
                <MenuItem value="this_month">This Month</MenuItem>
                <MenuItem value="last_month">Last Month</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md>
            <TextField
              size="small"
              fullWidth
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search invoice #, student, parent, email, phone…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <Tooltip title="Clear search">
                      <IconButton size="small" onClick={() => setSearch("")}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
                sx: controlSx,
              }}
            />
          </Grid>
        </Grid>

        {/* Secondary row: custom dates (when Custom preset) + Overdue toggle +
            Clear Filters + active chips + result count. */}
        <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
          {datePreset === "custom" && (
            <>
              <Box sx={{ minWidth: 160 }}>
                <AppDatePicker
                  label="From Date"
                  value={startDate}
                  onChange={(v) => { setStartDate(v); setPage(1); }}
                  fullWidth
                />
              </Box>
              <Box sx={{ minWidth: 160 }}>
                <AppDatePicker
                  label="To Date"
                  value={endDate}
                  onChange={(v) => { setEndDate(v); setPage(1); }}
                  fullWidth
                />
              </Box>
              {dateRangeInvalid && (
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: customBadgeColor(true) }}>
                  From Date must be on or before To Date.
                </Typography>
              )}
            </>
          )}

          <Tooltip title="Show invoices with an unpaid balance past the due date">
            <Box
              role="button"
              aria-pressed={overdueOnly}
              tabIndex={0}
              onClick={() => { setOverdueOnly((v) => !v); setPage(1); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOverdueOnly((v) => !v); setPage(1); } }}
              sx={{
                display: "inline-flex", alignItems: "center", gap: 0.75,
                px: 1.5, height: 34, borderRadius: "999px",
                cursor: "pointer", userSelect: "none",
                fontSize: 12.5, fontWeight: 800, letterSpacing: 0.2,
                border: "1.5px solid",
                transition: "all 0.15s ease",
                ...(overdueOnly
                  ? {
                      bgcolor: "#fef3c7", color: "#92400e",
                      borderColor: "#f59e0b",
                      boxShadow: "0 2px 6px rgba(245,158,11,0.25)",
                    }
                  : {
                      bgcolor: "#fffbeb", color: "#b45309",
                      borderColor: alpha("#f59e0b", 0.35),
                      "&:hover": { bgcolor: "#fef3c7", borderColor: "#f59e0b" },
                    }),
                "&:focus-visible": { outline: "2px solid #2563eb", outlineOffset: 2 },
              }}
            >
              <WarningAmberOutlinedIcon sx={{ fontSize: 15 }} />
              Overdue Only
              {overdueOnly && <CheckCircleOutlineIcon sx={{ fontSize: 15, ml: 0.25 }} />}
            </Box>
          </Tooltip>

          {activeFilterCount > 0 && (
            <Button
              size="small"
              variant="text"
              startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
              onClick={resetFilters}
              sx={{
                textTransform: "none", fontSize: 12.5, fontWeight: 700,
                borderRadius: "999px", px: 1.5, height: 34,
                color: "#475569", bgcolor: alpha("#64748b", 0.06),
                "&:hover": { bgcolor: alpha("#64748b", 0.12) },
              }}
            >
              Clear Filters
            </Button>
          )}

          {activeFilterCount > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {locationFilter && (
                <Chip
                  size="small"
                  label={`Location: ${locations.find((l: any) => String(l.id) === locationFilter)?.location_name || locationFilter}`}
                  onDelete={() => { setLocationFilter(""); setPage(1); }}
                  sx={{ bgcolor: alpha("#2563eb", 0.08), color: "#1d4ed8", fontWeight: 700, height: 26, "& .MuiChip-deleteIcon": { color: "#1d4ed8" } }}
                />
              )}
              {statusFilter && (
                <Chip
                  size="small"
                  label={`Status: ${STATUS_META[statusFilter]?.label || statusFilter}`}
                  onDelete={() => { setStatusFilter(""); setPage(1); }}
                  sx={{ bgcolor: alpha("#2563eb", 0.08), color: "#1d4ed8", fontWeight: 700, height: 26, "& .MuiChip-deleteIcon": { color: "#1d4ed8" } }}
                />
              )}
              {modeFilter && (
                <Chip
                  size="small"
                  label={`Mode: ${MODE_LABELS[modeFilter] || modeFilter}`}
                  onDelete={() => { setModeFilter(""); setPage(1); }}
                  sx={{ bgcolor: alpha("#2563eb", 0.08), color: "#1d4ed8", fontWeight: 700, height: 26, "& .MuiChip-deleteIcon": { color: "#1d4ed8" } }}
                />
              )}
              {datePreset && datePreset !== "custom" && (
                <Chip
                  size="small"
                  label={`Date: ${DATE_PRESET_LABELS[datePreset] || datePreset}`}
                  onDelete={() => applyDatePreset("")}
                  sx={{ bgcolor: alpha("#2563eb", 0.08), color: "#1d4ed8", fontWeight: 700, height: 26, "& .MuiChip-deleteIcon": { color: "#1d4ed8" } }}
                />
              )}
              {datePreset === "custom" && (startDate || endDate) && (
                <Chip
                  size="small"
                  label={`Date: ${startDate ? dayjs(startDate).format("MMM D, YYYY") : "…"} – ${endDate ? dayjs(endDate).format("MMM D, YYYY") : "…"}`}
                  onDelete={() => { setStartDate(""); setEndDate(""); setDatePreset(""); setPage(1); }}
                  sx={{ bgcolor: alpha("#2563eb", 0.08), color: "#1d4ed8", fontWeight: 700, height: 26, "& .MuiChip-deleteIcon": { color: "#1d4ed8" } }}
                />
              )}
              {search && (
                <Chip
                  size="small"
                  label={`Search: ${search}`}
                  onDelete={() => setSearch("")}
                  sx={{ bgcolor: alpha("#2563eb", 0.08), color: "#1d4ed8", fontWeight: 700, height: 26, "& .MuiChip-deleteIcon": { color: "#1d4ed8" } }}
                />
              )}
              {overdueOnly && (
                <Chip
                  size="small"
                  label="Overdue Only"
                  onDelete={() => { setOverdueOnly(false); setPage(1); }}
                  sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 700, height: 26, "& .MuiChip-deleteIcon": { color: "#92400e" } }}
                />
              )}
            </Box>
          )}

          {/* Result count pill — pinned far right so the whole row reads as
              "filter actions … | count". Spinner stays adjacent so loading
              never causes a layout shift. */}
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            {isFetching && (
              <Box sx={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid #dbeafe", borderTopColor: "#1d4ed8", animation: "spin 0.8s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } }} />
            )}
            <Box
              sx={{
                display: "inline-flex", alignItems: "center", gap: 0.75,
                px: 1.25, py: 0.5, borderRadius: "999px",
                bgcolor: alpha("#2563eb", 0.08), color: "#1d4ed8",
                fontSize: 12, fontWeight: 800, letterSpacing: 0.2,
              }}
            >
              <ReceiptLongIcon sx={{ fontSize: 14 }} />
              {isLoading ? "Loading…" : `${total} ${total === 1 ? "invoice" : "invoices"}`}
            </Box>
          </Box>
        </Box>
      </Box>
        );
      })()}

      {/* Table */}
      <Box sx={{ borderRadius: "18px", bgcolor: "#fff", border: "1px solid rgba(15,23,42,0.06)", boxShadow: "0 8px 24px rgba(15,23,42,0.05)", overflow: "hidden" }}>
        {isFetching && <LinearProgress />}
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontSize: 11, fontWeight: 800, letterSpacing: 0.3, textTransform: "uppercase", color: "#475569", bgcolor: "#f8fafc" } }}>
                <TableCell>Invoice #</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Parent / Guardian</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Issue Date</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 11 }).map((__, j) => (
                      <TableCell key={j}><Skeleton width={j === 0 ? 80 : "80%"} /></TableCell>
                    ))}
                  </TableRow>
                ))}
              {!isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Box sx={{ py: 6, textAlign: "center" }}>
                      <ReceiptLongIcon sx={{ fontSize: 44, color: "#cbd5e1" }} />
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#475569", mt: 1 }}>
                        {activeFilterCount > 0 ? "No invoices match the current filters." : "No invoices yet."}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.5 }}>
                        {activeFilterCount > 0 ? "Try adjusting your filters or clearing them to see everything." : "Invoices generated from class assignments or the monthly job will appear here."}
                      </Typography>
                      {activeFilterCount > 0 && (
                        <Button size="small" onClick={resetFilters} sx={{ mt: 2, textTransform: "none", fontWeight: 700 }}>
                          Reset Filters
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && rows.map((inv) => {
                const st = String(inv.effective_status || inv.status || "draft").toLowerCase();
                const meta = STATUS_META[st] || STATUS_META.draft;
                const isOverdue = st === "overdue" || inv.is_overdue;
                return (
                  <TableRow
                    key={inv.id}
                    hover
                    onClick={() => openInvoice(inv.id)}
                    sx={{
                      cursor: "pointer",
                      ...(isOverdue ? { bgcolor: alpha("#ef4444", 0.04) } : {}),
                    }}
                  >
                    <TableCell>
                      <Typography
                        onClick={(e) => { e.stopPropagation(); openInvoice(inv.id); }}
                        sx={{ fontSize: 13, fontWeight: 700, color: "#1d4ed8", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                      >
                        {inv.invoiceNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StudentProfileLink id={inv.studentId} name={inv.studentName} sx={{ fontSize: 13 }}>
                        {inv.studentName}
                      </StudentProfileLink>
                    </TableCell>
                    <TableCell>
                      {inv.parent_info?.id ? (
                        // parent_info.id is the STUDENT_PARENTS id, not a
                        // staff id — this cell must route to the Parent
                        // profile (/parents/view/:id). Using StaffProfileLink
                        // here was the "Galaxy Dong opens a staff record" bug.
                        <ParentProfileLink id={inv.parent_info.id} name={inv.parent_info.name} sx={{ fontSize: 13, color: "text.primary" }}>
                          {inv.parent_info.name}
                        </ParentProfileLink>
                      ) : (
                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{inv.parentName}</Typography>
                      )}
                    </TableCell>
                    <TableCell><Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>{inv.locationName}</Typography></TableCell>
                    <TableCell><Typography sx={{ fontSize: 12.5 }}>{fmtDate(inv.issueDate)}</Typography></TableCell>
                    <TableCell>
                      <Tooltip title={inv.dueDate ? dayjs(inv.dueDate).format("dddd, MMM D, YYYY") : ""}>
                        <Typography sx={{ fontSize: 12.5, color: isOverdue ? "#b91c1c" : "text.primary", fontWeight: isOverdue ? 700 : 400 }}>
                          {fmtDate(inv.dueDate)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right"><Typography sx={{ fontSize: 13, fontWeight: 700 }}>{money(inv.totalAmount)}</Typography></TableCell>
                    <TableCell align="right"><Typography sx={{ fontSize: 13, color: "#15803d", fontWeight: 600 }}>{money(inv.paid_amount)}</Typography></TableCell>
                    <TableCell align="right"><Typography sx={{ fontSize: 13, color: Number(inv.balance_due || 0) > 0 ? "#b45309" : "text.disabled", fontWeight: 700 }}>{money(inv.balance_due)}</Typography></TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={meta.label}
                        sx={{ height: 22, fontSize: 10.5, fontWeight: 800, bgcolor: meta.bg, color: meta.fg, border: `1px solid ${alpha(meta.fg, 0.2)}` }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <RowMenu
                        invoice={inv}
                        canEdit={canEditInvoice}
                        canCreate={canCreateInvoice}
                        canDelete={canDeleteInvoice}
                        onView={() => openInvoice(inv.id)}
                        onEdit={() => navigate(`/invoice/create?id=${inv.id}`)}
                        onSend={() => doSend(inv)}
                        onMarkSent={() => doMarkSent(inv)}
                        onRecordPayment={() => setPayFor(inv)}
                        onDuplicate={() => doDuplicate(inv)}
                        onVoid={() => setVoidFor(inv)}
                        onDelete={() => doDelete(inv)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Pagination */}
        {lastPage > 1 && (
          <Box sx={{ p: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid rgba(15,23,42,0.06)", flexWrap: "wrap", gap: 1 }}>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total}
            </Typography>
            <Pagination
              count={lastPage}
              page={page}
              onChange={(_, p) => setPage(p)}
              size="small"
              shape="rounded"
              color="primary"
            />
          </Box>
        )}
      </Box>

      {/* Record payment */}
      <RecordPaymentDialog
        invoice={payFor}
        open={!!payFor}
        onClose={() => setPayFor(null)}
      />

      {/* Void reason */}
      <VoidInvoiceDialog
        invoice={voidFor}
        open={!!voidFor}
        onClose={() => setVoidFor(null)}
      />
    </Box>
  );
};

export default InvoicesPage;
